import { useState } from 'react'
import { brandPalette, brandGradient } from '@cinqo/brand'
import { supabase } from '../lib/supabase'
import { normalizeEmail } from '../lib/email'

type Mode = 'login' | 'code' | 'reset'

/* ─── Left panel decorative SVG background ─── */
function HeroBg() {
  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Subtle grid */}
      <defs>
        <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.3" opacity="0.12" />
        </pattern>
      </defs>
      <rect width="800" height="900" fill="url(#grid)" />

      {/* Big blurred circles */}
      <circle cx="650" cy="150" r="320" fill="#5B47E0" opacity="0.35" />
      <circle cx="100" cy="750" r="260" fill={brandPalette.primaryDeep} opacity="0.45" />
      <circle cx="720" cy="820" r="180" fill="#6C5CE7" opacity="0.2" />

      {/* Geometric ring */}
      <circle cx="400" cy="450" r="300" stroke="white" strokeWidth="0.6" opacity="0.08" />
      <circle cx="400" cy="450" r="220" stroke="white" strokeWidth="0.6" opacity="0.06" />
      <circle cx="400" cy="450" r="140" stroke="white" strokeWidth="0.6" opacity="0.05" />

      {/* Corner accent lines */}
      <line x1="0" y1="0" x2="120" y2="120" stroke="white" strokeWidth="0.5" opacity="0.1" />
      <line x1="800" y1="900" x2="680" y2="780" stroke="white" strokeWidth="0.5" opacity="0.1" />
    </svg>
  )
}

/* ─── Cinqo wordmark (white) ─── */
function CinqoWordmark() {
  return (
    <svg width="120" height="30" viewBox="0 0 349 86" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M167.405 28.8097L168.845 30.3915L176.564 21.3834L175.334 20.1608C173.5 18.3376 171.426 16.7855 169.118 15.5047C166.822 14.2308 164.393 13.2493 161.834 12.5605C159.261 11.8678 156.634 11.5215 153.957 11.5215C149.816 11.5215 145.933 12.2349 142.326 13.6799L142.317 13.6836C138.783 15.1207 135.643 17.1478 132.908 19.7599L132.894 19.7729C130.207 22.3912 128.109 25.4396 126.607 28.905C125.09 32.3537 124.341 36.0809 124.341 40.0638C124.341 44.049 125.091 47.8199 126.601 51.3582L126.605 51.3677C128.104 54.8269 130.171 57.8728 132.805 60.4913L132.819 60.5045C135.503 63.1196 138.616 65.172 142.143 66.6596L142.166 66.6687C145.726 68.1164 149.562 68.8309 153.655 68.8309C156.344 68.8309 158.982 68.4565 161.563 67.7086C164.175 66.9663 166.655 65.906 168.999 64.5297C171.392 63.1544 173.531 61.5343 175.41 59.6672L176.714 58.3704L168.847 50.2148L167.487 51.5332C165.639 53.3246 163.541 54.7469 161.184 55.8052C158.851 56.8038 156.496 57.2941 154.108 57.2941C151.746 57.2941 149.544 56.8447 147.48 55.954C145.439 55.0511 143.627 53.8135 142.036 52.2336C140.503 50.6632 139.307 48.8472 138.444 46.7718C137.586 44.6595 137.152 42.4039 137.152 39.9889C137.152 37.5163 137.588 35.2645 138.441 33.2125L138.446 33.1994C139.311 31.0721 140.508 29.2346 142.036 27.6692C143.622 26.0945 145.426 24.8865 147.455 24.0346L147.474 24.0267C149.539 23.1341 151.744 22.6837 154.108 22.6837C156.476 22.6837 158.845 23.2149 161.231 24.3093C163.615 25.4033 165.669 26.9014 167.405 28.8097Z" fill="white"/>
      <path d="M191.581 24.7064H179.222V68.2315H191.581V24.7064Z" fill="white"/>
      <path d="M190.351 20.1125L190.368 20.0955L190.384 20.0781C191.677 18.7254 192.334 17.0628 192.334 15.1923C192.334 13.2907 191.688 11.6019 190.351 10.2721C189.013 8.94237 187.314 8.30019 185.401 8.30019C183.52 8.30019 181.847 8.95354 180.486 10.2386L180.469 10.2551L180.452 10.2721C179.114 11.6019 178.468 13.2907 178.468 15.1923C178.468 17.0628 179.125 18.7254 180.418 20.0781L180.451 20.1129L180.486 20.146C181.847 21.4311 183.52 22.0844 185.401 22.0844C187.314 22.0844 189.013 21.4422 190.351 20.1125Z" fill="white"/>
      <path d="M221.072 35.7937C223.679 35.7963 225.497 36.5602 226.773 37.9037L226.791 37.9227C228.072 39.2334 228.821 41.1374 228.821 43.8844V68.2315H241.255V41.637C241.255 38.2197 240.612 35.1863 239.237 32.6118C237.913 29.9898 235.942 27.9473 233.361 26.5198C230.811 25.0816 227.815 24.4067 224.45 24.4067C220.682 24.4067 217.302 25.1569 214.369 26.725C213.349 27.2625 212.414 27.9103 211.564 28.663V24.7064H199.13V68.2315H211.564V45.2081C211.812 42.6656 212.666 40.5741 214.071 38.8597C215.734 36.8888 217.997 35.8397 221.072 35.7937Z" fill="white"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M275.278 65.9173C275.932 65.5047 276.55 65.0537 277.132 64.565V82.7649H289.566V24.7064H277.132V28.4806C276.527 27.9754 275.883 27.5126 275.199 27.093C272.275 25.2176 268.766 24.3318 264.773 24.3318C260.711 24.3318 257.053 25.2506 253.861 27.1433C250.689 29.0241 248.217 31.6551 246.454 34.9925C244.679 38.3033 243.824 42.1103 243.824 46.3566C243.824 50.6068 244.681 54.4548 246.445 57.8534L246.451 57.8644C248.211 61.2003 250.679 63.8523 253.84 65.7822L253.857 65.7922L253.873 65.8019C257.112 67.6894 260.816 68.6061 264.924 68.6061C268.87 68.6061 272.349 67.7441 275.27 65.9224L275.278 65.9173ZM261.322 56.6025C259.764 55.6396 258.537 54.325 257.633 52.6191C256.782 50.9204 256.308 48.9218 256.258 46.5813C256.308 44.2372 256.784 42.2359 257.637 40.5355C258.545 38.7761 259.773 37.4427 261.322 36.4853C262.852 35.5396 264.636 35.0446 266.733 35.0446C268.83 35.0446 270.613 35.5396 272.143 36.4853C273.698 37.4462 274.895 38.7552 275.748 40.4519L275.757 40.4685L275.765 40.4849C276.656 42.1711 277.132 44.1851 277.132 46.5813C277.132 48.9212 276.659 50.9117 275.765 52.6029L275.757 52.6193L275.748 52.6359C274.895 54.3326 273.698 55.6415 272.143 56.6025C270.613 57.5482 268.83 58.0432 266.733 58.0432C264.636 58.0432 262.852 57.5482 261.322 56.6025Z" fill="white"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M316.517 24.4067C320.901 24.4067 324.837 25.3117 328.27 27.182C331.733 29.0414 334.451 31.6565 336.385 35.0068C338.333 38.3293 339.276 42.1585 339.276 46.4315C339.276 50.7082 338.332 54.5591 336.388 57.9265C334.453 61.2791 331.735 63.8958 328.27 65.7561C324.837 67.6263 320.901 68.5312 316.517 68.5312C312.131 68.5312 308.173 67.6252 304.696 65.7594C301.228 63.8989 298.507 61.2811 296.571 57.9265C294.628 54.5591 293.684 50.7083 293.684 46.4315C293.684 42.1578 294.626 38.328 296.575 35.0052C298.511 31.6536 301.23 29.0379 304.696 27.1785C308.173 25.3128 312.131 24.4067 316.517 24.4067ZM311.075 36.5794L311.091 36.57L311.107 36.5602C312.637 35.6145 314.42 35.1195 316.517 35.1195C318.615 35.1195 320.364 35.6147 321.833 36.5479L321.859 36.5641L321.885 36.5794C323.421 37.4875 324.608 38.7619 325.458 40.4519L325.466 40.4685L325.475 40.4849C326.368 42.176 326.841 44.1666 326.841 46.5064C326.841 48.9026 326.365 50.9166 325.475 52.6029L325.466 52.6193L325.458 52.6359C324.606 54.3302 323.411 55.6378 321.86 56.5983C320.387 57.4915 318.628 57.9683 316.517 57.9683C314.412 57.9683 312.622 57.4938 311.091 56.5925C309.54 55.6306 308.319 54.3192 307.418 52.6191C306.571 50.9288 306.118 48.9082 306.118 46.5064C306.118 44.1606 306.569 42.1635 307.418 40.4687C308.318 38.7696 309.535 37.4894 311.075 36.5794Z" fill="white"/>
      <path d="M37.9009 70.4764L42.963 79.1926C44.3118 81.5151 47.6839 81.5151 49.0327 79.1926L54.0949 70.4764H37.9009Z" fill="white"/>
      <path d="M75.965 32.8194L91.521 6.03437C92.8698 3.71189 91.1838 0.808784 88.4861 0.808784L21.6984 0.808778V14.2174H54.4493C58.4647 14.2174 62.1246 15.2362 65.429 17.2736C68.7333 19.3111 71.3684 22.0346 73.3343 25.4443C74.6545 27.7339 75.5313 30.1923 75.965 32.8194Z" fill="white"/>
      <path d="M8.52283 0.808777L3.5096 0.808777C0.811936 0.808777 -0.874109 3.71188 0.474724 6.03436L8.52283 19.892V0.808777Z" fill="white"/>
      <path d="M12.653 27.0036L30.2577 57.316H54.3238C56.7498 57.316 58.8202 56.4844 60.5352 54.8212C62.2501 53.158 63.1075 51.1413 63.1075 48.7712V36.6088C63.1075 33.9476 62.2501 31.6814 60.5352 29.8103C58.8621 27.9391 56.8125 27.0036 54.3865 27.0036H12.653Z" fill="white"/>
    </svg>
  )
}

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      if (mode === 'login') {
        const { error: err } = await supabase.auth.signInWithPassword({ email: normalizeEmail(email), password })
        if (err) {
          if (err.message.includes('Invalid login')) {
            setError('Email ou mot de passe incorrect.')
          } else if (err.message.includes('Email not confirmed')) {
            setError("Votre compte n'est pas encore activé. Vérifiez votre boîte mail.")
          } else {
            setError(err.message)
          }
        }
      } else if (mode === 'code') {
        // Connexion par CODE à 8 chiffres. Robuste aux scanners de liens des
        // messageries pro (Safe Links / Mimecast / Proofpoint) qui pré-cliquent
        // et crament les liens magiques à usage unique : on ne clique rien, on
        // saisit le code. L'email (template Supabase « Magic Link » + {{ .Token }})
        // contient le code. shouldCreateUser:false = anti-enumeration.
        if (!codeSent) {
          const { error: err } = await supabase.auth.signInWithOtp({
            email: normalizeEmail(email),
            options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
          })
          if (err) setError(err.message)
          else setCodeSent(true)
        } else {
          const { error: err } = await supabase.auth.verifyOtp({
            email: normalizeEmail(email),
            token: code.trim(),
            type: 'email',
          })
          if (err) setError('Code incorrect ou expiré. Demandez-en un nouveau.')
          // Succès : la session est posée, App.tsx bascule automatiquement.
        }
      } else if (mode === 'reset') {
        const { error: err } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
          redirectTo: window.location.origin,
        })
        if (err) setError('Erreur : ' + err.message)
        else setSuccess('Si un compte existe, vous recevrez un lien pour réinitialiser votre mot de passe.')
      }
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (newMode: Mode) => {
    setMode(newMode)
    setError('')
    setSuccess('')
    setPassword('')
    setCode('')
    setCodeSent(false)
  }

  return (
    <div className="min-h-screen flex">

      {/* ═══ LEFT — hero panel ═══ */}
      <div className="hidden lg:flex lg:w-[58%] relative flex-col overflow-hidden" style={{ background: brandGradient.hero }}>
        <HeroBg />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <CinqoWordmark />
        </div>

        {/* Content — left-aligned, site-astro style */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-12">
          <h2 className="text-white text-h1 font-extrabold leading-[1.15] max-w-[480px]">
            Vos déchets industriels.{' '}
            <span className="text-success">Pilotés. Optimisés.</span>
          </h2>

          <p className="mt-5 text-white/60 text-body leading-relaxed max-w-[400px]">
            Un seul interlocuteur multi-flux, conformité réglementaire et reporting RSE, prêt pour vos audits.
          </p>

          {/* Trust signals */}
          <div className="mt-8 flex flex-col gap-2.5">
            {['Conformité ISO 14001', 'Reporting RSE automatisé', 'Traçabilité complète'].map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-data text-white/70">
                <svg className="w-4 h-4 text-success flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                {t}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 px-12 pb-8">
          <p className="text-white/25 text-caption">cinqo.fr · WasteX SAS</p>
        </div>
      </div>

      {/* ═══ RIGHT — form panel ═══ */}
      <div className="flex-1 flex flex-col bg-surface-card">

        {/* Status bar */}
        <div className="flex justify-end px-8 pt-6">
          <span className="inline-flex items-center gap-1.5 text-caption text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            Le serveur est accessible
          </span>
        </div>

        {/* Form area — centered */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
          <div className="w-full max-w-[360px]">

            {/* Mobile logo */}
            <div className="lg:hidden mb-8 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                <svg viewBox="0 0 92 86" className="w-4 h-4" fill="none">
                  <path d="M37.9009 70.4764L42.963 79.1926C44.3118 81.5151 47.6839 81.5151 49.0327 79.1926L54.0949 70.4764H37.9009Z" fill="white"/>
                  <path d="M75.965 32.8194L91.521 6.03437C92.8698 3.71189 91.1838 0.808784 88.4861 0.808784L21.6984 0.808778V14.2174H54.4493C58.4647 14.2174 62.1246 15.2362 65.429 17.2736C68.7333 19.3111 71.3684 22.0346 73.3343 25.4443C74.6545 27.7339 75.5313 30.1923 75.965 32.8194Z" fill="white"/>
                  <path d="M8.52283 0.808777L3.5096 0.808777C0.811936 0.808777 -0.874109 3.71188 0.474724 6.03436L8.52283 19.892V0.808777Z" fill="white"/>
                  <path d="M12.653 27.0036L30.2577 57.316H54.3238C56.7498 57.316 58.8202 56.4844 60.5352 54.8212C62.2501 53.158 63.1075 51.1413 63.1075 48.7712V36.6088C63.1075 33.9476 62.2501 31.6814 60.5352 29.8103C58.8621 27.9391 56.8125 27.0036 54.3865 27.0036H12.653Z" fill="white"/>
                </svg>
              </div>
              <span className="text-h3 font-bold text-violet-500">Cinqo</span>
            </div>

            {success ? (
              /* ── Success state ── */
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-success-bg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <h2 className="text-body font-semibold text-ink mb-2">Email envoyé</h2>
                <p className="text-data text-ink-muted leading-relaxed mb-5">{success}</p>
                <button onClick={() => switchMode('login')} className="text-data text-violet-500 font-medium hover:underline cursor-pointer">
                  ← Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {/* Heading */}
                <div className="mb-6">
                  <h1 className="text-h2 font-bold text-ink">
                    {mode === 'login' ? 'Connexion' : mode === 'code' ? 'Connexion par code' : 'Mot de passe oublié'}
                  </h1>
                  {mode === 'login' && (
                    <p className="text-data text-ink-muted mt-1">
                      Pas encore de compte ?{' '}
                      <a href="mailto:contact@cinqo.fr" className="text-violet-500 hover:underline">Contactez-nous</a>
                    </p>
                  )}
                  {mode !== 'login' && (
                    <button type="button" onClick={() => switchMode('login')} className="text-data text-violet-500 hover:underline cursor-pointer mt-1">
                      ← Retour à la connexion
                    </button>
                  )}
                </div>

                {/* Fields */}
                <div className="space-y-3 mb-5">
                  <div>
                    <label className="block text-caption font-medium text-ink-secondary mb-1.5">E-mail</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setError('') }}
                      placeholder="john.doe@company.com"
                      required
                      autoComplete="email"
                      disabled={mode === 'code' && codeSent}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-ink-ghost text-body text-ink placeholder:text-ink-ghost focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-500 transition-all disabled:opacity-60 disabled:bg-surface-low"
                    />
                  </div>

                  {mode === 'login' && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-caption font-medium text-ink-secondary">Mot de passe</label>
                        <button
                          type="button"
                          onClick={() => switchMode('reset')}
                          className="text-caption text-violet-500 hover:underline cursor-pointer"
                        >
                          Mot de passe oublié ?
                        </button>
                      </div>
                      <input
                        type="password"
                        value={password}
                        onChange={e => { setPassword(e.target.value); setError('') }}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        autoComplete="current-password"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-ink-ghost text-body text-ink placeholder:text-ink-ghost focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-500 transition-all"
                      />
                    </div>
                  )}

                  {mode === 'code' && !codeSent && (
                    <p className="text-caption text-ink-muted leading-relaxed bg-surface-low rounded-lg px-3 py-2.5 border border-ink-ghost/30">
                      Un code à 8 chiffres vous sera envoyé par email. Pas besoin de mot de passe — et ça fonctionne même si votre messagerie d'entreprise bloque les liens.
                    </p>
                  )}

                  {mode === 'code' && codeSent && (
                    <div>
                      <label className="block text-caption font-medium text-ink-secondary mb-1.5">Code reçu par email</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 8)); setError('') }}
                        placeholder="00000000"
                        maxLength={8}
                        autoFocus
                        className="w-full px-3.5 py-2.5 rounded-lg border border-ink-ghost text-h3 text-ink tracking-[0.3em] text-center placeholder:tracking-[0.15em] placeholder:text-base placeholder:text-ink-ghost focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-500 transition-all"
                      />
                      <p className="text-caption text-ink-muted mt-1.5 leading-relaxed">
                        Code envoyé à <strong>{email}</strong>.{' '}
                        <button type="button" onClick={() => { setCode(''); setCodeSent(false); setError('') }} className="text-violet-500 hover:underline cursor-pointer">
                          Renvoyer / changer d'email
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="flex gap-2 items-start mb-4 p-3 rounded-lg bg-error-bg border border-error/30">
                    <svg className="w-4 h-4 text-error flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-caption text-error leading-relaxed">{error}</p>
                  </div>
                )}

                {/* Primary CTA */}
                <button
                  type="submit"
                  disabled={loading || !email || (mode === 'login' && !password) || (mode === 'code' && codeSent && code.trim().length < 8)}
                  className="w-full px-4 py-2.5 rounded-lg bg-violet-500 text-white text-body font-semibold hover:bg-violet-600 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-violet-500/20"
                >
                  {loading
                    ? 'Chargement...'
                    : mode === 'login'
                      ? 'Se connecter'
                      : mode === 'code'
                        ? (codeSent ? 'Se connecter' : 'Recevoir un code')
                        : 'Réinitialiser'}
                </button>

                {/* Magic link */}
                {mode === 'login' && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => switchMode('code')}
                      className="text-caption text-ink-muted hover:text-violet-500 transition-colors cursor-pointer"
                    >
                      Se connecter par code email
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-6 text-center">
          <p className="text-caption text-ink-ghost">© {new Date().getFullYear()} WasteX SAS (Cinqo)</p>
        </div>
      </div>
    </div>
  )
}
